import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Banknote,
  ExternalLink,
} from 'lucide-react';
import { PageGuard } from '@components/PageGuard';
import { TreasuryOperationModal } from '@components/finance/TreasuryOperationModal';
import { ClearStrandedHoldModal } from '@components/finance/ClearStrandedHoldModal';
import {
  ledgerService,
  MONEY_TRANSACTION_TYPES,
  HELD_INCOMING_NOTE,
  RAW_AVAILABLE_NOTE,
  type LedgerTransactionType,
  type WalletStatementRow,
} from '@services/ledger.service';
import { cn, formatCurrency } from '@lib/utils';
import {
  chipTxnType,
  formatTimestamp,
  humanizeCode,
  labelTxnType,
  ownerTypeLabel,
  shortId,
  toIso,
} from './ledgerFormat';

const PAGE_SIZE = 50;
const CHART_SIZE = 200;

const inputClass =
  'rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors';

const thClass =
  'text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] whitespace-nowrap';

interface ChartPoint {
  label: string;
  runningBalance: number;
}

const ChartTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartPoint }>;
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-2xl bg-[var(--bg-surface)] shadow-lift border border-[var(--border-subtle)] p-3 text-[13px]">
      <p className="font-semibold text-[var(--text-primary)]">{point.label}</p>
      <p className="text-[var(--text-accent)] font-medium mt-1">
        Balance: {formatCurrency(point.runningBalance)}
      </p>
    </div>
  );
};

/**
 * `wallet_transactions.amount` is always stored positive — the same type can be a
 * debit on one wallet and a credit on another. The only trustworthy direction
 * signal is how the running balance actually moved, so it is derived from
 * consecutive rows in chronological order. The oldest row on a page has no
 * predecessor, so its direction stays unknown rather than guessed.
 */
function buildBalanceDeltas(
  rows: WalletStatementRow[],
  order: 'asc' | 'desc',
): Map<string, number> {
  const chronological = order === 'asc' ? rows : [...rows].slice().reverse();
  const deltas = new Map<string, number>();
  for (let i = 1; i < chronological.length; i += 1) {
    deltas.set(
      chronological[i].id,
      Number(
        (chronological[i].runningBalance - chronological[i - 1].runningBalance).toFixed(2),
      ),
    );
  }
  return deltas;
}

export const WalletDetailPage = () => {
  const { walletId = '' } = useParams<{ walletId: string }>();

  const [transactionType, setTransactionType] = useState<'' | LedgerTransactionType>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [treasuryOpen, setTreasuryOpen] = useState(false);
  const [clearHoldOpen, setClearHoldOpen] = useState(false);

  const filters = useMemo(
    () => ({
      transactionType: transactionType || undefined,
      from: toIso(from),
      to: toIso(to),
    }),
    [transactionType, from, to],
  );

  const {
    data: wallet,
    isLoading: walletLoading,
    error: walletError,
  } = useQuery({
    queryKey: ['ledger', 'wallet', walletId],
    queryFn: () => ledgerService.getWallet(walletId),
    enabled: !!walletId,
  });

  const {
    data: statement,
    isLoading: statementLoading,
    error: statementError,
  } = useQuery({
    queryKey: ['ledger', 'wallet-statement', walletId, filters, page],
    queryFn: () =>
      ledgerService.getWalletTransactions(walletId, {
        ...filters,
        order: 'desc',
        page,
        size: PAGE_SIZE,
      }),
    enabled: !!walletId,
    placeholderData: keepPreviousData,
  });

  // Chronological series for the running-balance chart.
  const { data: series, isLoading: seriesLoading } = useQuery({
    queryKey: ['ledger', 'wallet-series', walletId, filters],
    queryFn: () =>
      ledgerService.getWalletTransactions(walletId, {
        ...filters,
        order: 'asc',
        page: 1,
        size: CHART_SIZE,
      }),
    enabled: !!walletId,
  });

  const rows = statement?.data ?? [];
  const totalCount = statement?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const deltas = useMemo(
    () => buildBalanceDeltas(rows, statement?.order ?? 'desc'),
    [rows, statement?.order],
  );

  const chartData: ChartPoint[] = useMemo(
    () =>
      (series?.data ?? []).map((row) => ({
        label: row.createdAt
          ? new Date(row.createdAt).toLocaleDateString('en-KE', {
              month: 'short',
              day: 'numeric',
            })
          : '—',
        runningBalance: row.runningBalance,
      })),
    [series],
  );

  // A negative raw `available` on its own is normal in-flight escrow and must
  // NOT raise a panel. Only stranded holds (nothing will ever release them) and
  // held-balance drift (the wallet row disagrees with its own log) are faults.
  const needsExplanation =
    !!wallet && (wallet.heldStranded > 0 || wallet.heldDrift !== 0);

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        <Link
          to="/finance/wallets"
          className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          All wallets
        </Link>

        {walletLoading && (
          <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-12 text-center text-[14px] text-[var(--text-muted)]">
            Loading wallet...
          </div>
        )}

        {!!walletError && !walletLoading && (
          <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-12 text-center text-[14px] text-[var(--state-danger)]">
            Failed to load this wallet.
          </div>
        )}

        {wallet && (
          <>
            {/* Identity + balances */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                  {ownerTypeLabel[wallet.ownerType] ?? wallet.ownerType} wallet
                </p>
                <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                  {wallet.ownerLabel ?? humanizeCode(wallet.typeCode)}
                </h1>
                <p className="text-[14px] text-[var(--text-muted)]">
                  {wallet.typeName || humanizeCode(wallet.typeCode)} · {wallet.typeCode} ·{' '}
                  {wallet.status}
                </p>

                {/* Every wallet this owner holds, plus their holds and withdrawals.
                    System wallets have no owner ledger. */}
                {wallet.ownerId && wallet.ownerType !== 'system' && (
                  <Link
                    to={`/finance/owners/${wallet.ownerType}/${wallet.ownerId}`}
                    className="inline-flex items-center gap-1.5 pt-1 text-[13px] font-medium text-[var(--text-accent)] hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                    All of this owner's money
                  </Link>
                )}
              </div>

              <button
                type="button"
                onClick={() => setTreasuryOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-colors"
              >
                <Banknote className="w-4 h-4" strokeWidth={1.5} />
                Manage money
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-6">
                <div className="text-[13px] text-[var(--text-muted)]">Balance</div>
                <div className="font-display text-[32px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-2 leading-none">
                  {formatCurrency(wallet.balance, wallet.currency)}
                </div>
                <p className="text-[12px] text-[var(--text-muted)] mt-3">
                  What is in this wallet right now.
                </p>
              </div>

              {/* Incoming is the normal state of a live order — muted, not danger. */}
              <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-6">
                <div className="text-[13px] text-[var(--text-muted)]">
                  Incoming (earmarked)
                </div>
                <div className="font-display text-[32px] font-medium tracking-[-0.03em] text-[var(--text-secondary)] mt-2 leading-none">
                  {formatCurrency(wallet.heldIncoming, wallet.currency)}
                </div>
                <p className="text-[12px] text-[var(--text-muted)] mt-3">
                  {HELD_INCOMING_NOTE}.
                </p>
              </div>

              <div
                className={cn(
                  'rounded-3xl p-6 shadow-soft',
                  wallet.heldStranded > 0
                    ? 'border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)]'
                    : 'bg-[var(--bg-surface)]',
                )}
              >
                <div className="text-[13px] text-[var(--text-muted)]">Stranded</div>
                <div
                  className={cn(
                    'font-display text-[32px] font-medium tracking-[-0.03em] mt-2 leading-none',
                    wallet.heldStranded > 0
                      ? 'text-[var(--state-danger)]'
                      : 'text-[var(--text-primary)]',
                  )}
                >
                  {formatCurrency(wallet.heldStranded, wallet.currency)}
                </div>
                {wallet.heldStranded > 0 ? (
                  <button
                    type="button"
                    onClick={() => setClearHoldOpen(true)}
                    className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--state-danger)] hover:underline"
                  >
                    <Banknote className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Clear it — review and clear
                  </button>
                ) : (
                  <p className="text-[12px] text-[var(--text-muted)] mt-3">
                    Nothing held against a finished basket.
                  </p>
                )}
              </div>

              <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-6">
                <div className="text-[13px] text-[var(--text-muted)]">Spendable</div>
                <div className="font-display text-[32px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-2 leading-none">
                  {formatCurrency(wallet.spendable, wallet.currency)}
                </div>
                <p className="text-[12px] text-[var(--text-muted)] mt-3">
                  Balance minus holds that genuinely lock it — what the holder can
                  use today.
                </p>
              </div>
            </div>

            {/* The DB's own figures stay on screen — demoted, never hidden. */}
            <p className="text-[12px] text-[var(--text-muted)]">
              {RAW_AVAILABLE_NOTE}:{' '}
              <span className="font-medium text-[var(--text-secondary)]">
                {formatCurrency(wallet.availableBalance, wallet.currency)}
              </span>{' '}
              · held {formatCurrency(wallet.heldBalance, wallet.currency)} · locked on
              balance {formatCurrency(wallet.heldOnBalance, wallet.currency)}.{' '}
              {wallet.availableBalance < 0 && wallet.heldStranded === 0
                ? 'Negative because escrow has earmarked money FOR this wallet that has not landed yet — normal while orders are in flight.'
                : 'Generated column kept for auditability.'}
            </p>

            {/* Stranded holds / held drift — the two things that are genuinely wrong */}
            {needsExplanation && (
              <div className="rounded-3xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    className="w-5 h-5 shrink-0 text-[var(--state-danger)] mt-0.5"
                    strokeWidth={1.5}
                  />
                  <div className="space-y-2">
                    <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">
                      {wallet.heldStranded > 0
                        ? 'This wallet has stranded holds'
                        : 'This wallet has held-balance drift'}
                    </h2>
                    {wallet.heldStranded > 0 && (
                      <p className="text-[13px] text-[var(--text-secondary)]">
                        <span className="font-semibold">
                          {formatCurrency(wallet.heldStranded, wallet.currency)}
                        </span>{' '}
                        is still held against baskets that already reached a terminal
                        status. Nothing in the system will ever release it — clear it
                        from the treasury adjust-hold worklist.
                      </p>
                    )}
                    <p className="text-[13px] text-[var(--text-secondary)]">
                      A negative <span className="font-semibold">raw available</span>{' '}
                      (balance − held) is <span className="font-semibold">not</span> a
                      fault by itself: escrow records a merchant's or Chekit's share as
                      a hold on the DESTINATION wallet while the cash stays in
                      chekit_escrow, so the earmark is subtracted from a balance that
                      never contained it. Read{' '}
                      <span className="font-semibold">spendable</span> instead.
                    </p>
                    <p className="text-[13px] text-[var(--text-secondary)]">
                      Below, <span className="font-semibold">stored held</span> is the
                      value on the wallet row, and{' '}
                      <span className="font-semibold">recomputed held</span> is what the
                      transaction history says should be held (holds minus releases).
                      Any gap between the two is a bookkeeping mismatch.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-[var(--state-danger-border)]">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Stored held
                    </div>
                    <div className="text-[18px] font-semibold text-[var(--text-primary)]">
                      {formatCurrency(wallet.heldBalance, wallet.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Recomputed held
                    </div>
                    <div className="text-[18px] font-semibold text-[var(--text-primary)]">
                      {formatCurrency(wallet.heldOutstanding, wallet.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Drift (stored − recomputed)
                    </div>
                    <div
                      className={cn(
                        'text-[18px] font-semibold',
                        wallet.heldDrift === 0
                          ? 'text-[var(--state-success)]'
                          : 'text-[var(--state-danger)]',
                      )}
                    >
                      {formatCurrency(wallet.heldDrift, wallet.currency)}
                    </div>
                  </div>
                </div>

                <p className="text-[12px] text-[var(--text-muted)]">
                  {wallet.heldDrift === 0
                    ? 'Holds reconcile exactly against the transaction log — the stranded amount above is a real, un-releasable earmark rather than a bookkeeping mismatch.'
                    : wallet.heldDrift > 0
                      ? 'Stored held is higher than the history supports — a hold was applied to the wallet row without a matching hold transaction, or a release updated the transaction log but not the wallet.'
                      : 'Stored held is lower than the history supports — holds are recorded in the log that the wallet row never picked up.'}
                </p>
              </div>
            )}

            {/* Running balance */}
            <section className="space-y-4">
              <div className="space-y-1">
                <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                  How the balance changed
                </h2>
                <p className="text-[13px] text-[var(--text-muted)]">
                  Balance after each transaction, oldest first (first {CHART_SIZE}{' '}
                  transactions matching the filters).
                </p>
              </div>

              <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-6">
                {seriesLoading && (
                  <div className="h-[300px] flex items-center justify-center text-[14px] text-[var(--text-muted)]">
                    Loading balance history...
                  </div>
                )}

                {!seriesLoading && chartData.length === 0 && (
                  <div className="h-[300px] flex items-center justify-center text-[14px] text-[var(--text-muted)]">
                    No transactions to chart.
                  </div>
                )}

                {!seriesLoading && chartData.length > 0 && (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="walletBalanceGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
                        <XAxis
                          dataKey="label"
                          stroke="var(--text-muted)"
                          tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                          minTickGap={24}
                        />
                        <YAxis
                          stroke="var(--text-muted)"
                          tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                          width={90}
                        />
                        <Tooltip content={<ChartTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="runningBalance"
                          stroke="var(--chart-1)"
                          strokeWidth={3}
                          fill="url(#walletBalanceGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </section>

            {/* Statement */}
            <section className="space-y-4">
              <div className="space-y-1">
                <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                  Statement
                </h2>
                <p className="text-[13px] text-[var(--text-muted)]">
                  Newest first. The balance change column is derived from consecutive
                  running balances — a hold moves no balance, it only locks it.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={transactionType}
                  onChange={(e) => {
                    setTransactionType(e.target.value as '' | LedgerTransactionType);
                    setPage(1);
                  }}
                  className={cn(inputClass, 'w-56')}
                >
                  <option value="">All transaction types</option>
                  {MONEY_TRANSACTION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {labelTxnType(type)}
                    </option>
                  ))}
                </select>

                <label className="flex items-center gap-2 text-[13px] text-[var(--text-muted)]">
                  From
                  <input
                    type="datetime-local"
                    value={from}
                    onChange={(e) => {
                      setFrom(e.target.value);
                      setPage(1);
                    }}
                    className={cn(inputClass, 'w-56')}
                  />
                </label>

                <label className="flex items-center gap-2 text-[13px] text-[var(--text-muted)]">
                  To
                  <input
                    type="datetime-local"
                    value={to}
                    onChange={(e) => {
                      setTo(e.target.value);
                      setPage(1);
                    }}
                    className={cn(inputClass, 'w-56')}
                  />
                </label>

                {(transactionType || from || to) && (
                  <button
                    type="button"
                    onClick={() => {
                      setTransactionType('');
                      setFrom('');
                      setTo('');
                      setPage(1);
                    }}
                    className="rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft overflow-hidden">
                {statementLoading && (
                  <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
                    Loading statement...
                  </div>
                )}

                {!!statementError && !statementLoading && (
                  <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
                    Failed to load the statement.
                  </div>
                )}

                {statement && !statementLoading && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[var(--bg-base)]">
                        <tr>
                          <th className={thClass}>When</th>
                          <th className={thClass}>Type</th>
                          <th className={thClass}>Amount</th>
                          <th className={thClass}>Balance change</th>
                          <th className={thClass}>Balance after</th>
                          <th className={thClass}>Moved to / from</th>
                          <th className={thClass}>Description</th>
                          <th className={thClass}>Reference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border-subtle)]">
                        {rows.map((row) => {
                          const delta = deltas.get(row.id);
                          return (
                            <tr key={row.id} className="hover:bg-[var(--bg-base)] transition-colors">
                              <td className="px-6 py-4 text-[13px] text-[var(--text-muted)] whitespace-nowrap">
                                {formatTimestamp(row.createdAt)}
                              </td>
                              <td className="px-6 py-4">
                                <span
                                  className={cn(
                                    'rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap',
                                    chipTxnType(row.transactionType),
                                  )}
                                >
                                  {labelTxnType(row.transactionType)}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-[14px] font-medium text-[var(--text-primary)] whitespace-nowrap">
                                {formatCurrency(row.amount)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {delta === undefined ? (
                                  <span className="text-[13px] text-[var(--text-muted)]">—</span>
                                ) : delta > 0 ? (
                                  <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-[var(--state-success)]">
                                    <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    +{formatCurrency(delta)}
                                  </span>
                                ) : delta < 0 ? (
                                  <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-[var(--state-danger)]">
                                    <ArrowDownRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    {formatCurrency(delta)}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[13px] text-[var(--text-muted)]">
                                    <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    No balance move
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-[14px] font-medium text-[var(--text-primary)] whitespace-nowrap">
                                {formatCurrency(row.runningBalance)}
                              </td>
                              <td className="px-6 py-4 text-[13px]">
                                {row.counterpartyWalletId ? (
                                  <Link
                                    to={`/finance/wallets/${row.counterpartyWalletId}`}
                                    className="text-[var(--text-accent)] hover:underline"
                                  >
                                    {row.counterpartyLabel ?? shortId(row.counterpartyWalletId)}
                                  </Link>
                                ) : (
                                  <span className="text-[var(--text-muted)]">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-[13px] text-[var(--text-secondary)]">
                                <span className="block max-w-[280px] truncate">
                                  {row.description ?? '—'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-[13px] whitespace-nowrap">
                                {row.referenceType === 'order' && row.referenceId ? (
                                  <Link
                                    to={`/finance/trace/${row.referenceId}`}
                                    className="text-[var(--text-accent)] hover:underline"
                                  >
                                    Trace order {shortId(row.referenceId)}
                                  </Link>
                                ) : row.referenceType ? (
                                  <span className="text-[var(--text-muted)]">
                                    {row.referenceType} {shortId(row.referenceId)}
                                  </span>
                                ) : (
                                  <span className="text-[var(--text-muted)]">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}

                        {rows.length === 0 && (
                          <tr>
                            <td
                              colSpan={8}
                              className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]"
                            >
                              No transactions match these filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {statement && totalCount > 0 && (
                  <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-[var(--border-subtle)]">
                    <span className="text-[13px] text-[var(--text-muted)]">
                      Page {page} of {lastPage} · {totalCount} transactions
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="inline-flex items-center gap-1 rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[var(--border-strong)] transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                        disabled={page >= lastPage}
                        className="inline-flex items-center gap-1 rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[var(--border-strong)] transition-colors"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {wallet && (
        <TreasuryOperationModal
          open={treasuryOpen}
          onClose={() => setTreasuryOpen(false)}
          walletId={wallet.walletId}
          walletLabel={wallet.ownerLabel}
          walletTypeCode={wallet.typeCode}
        />
      )}

      {/* Clearing a stranded hold is NOT a generic treasury operation: the
          amount is dictated by one basket's outstanding earmark, not typed by
          the operator, and the wallet's held_balance is not a safe ceiling. */}
      {wallet && (
        <ClearStrandedHoldModal
          open={clearHoldOpen}
          onClose={() => setClearHoldOpen(false)}
          walletId={wallet.walletId}
          walletLabel={wallet.ownerLabel}
        />
      )}
    </PageGuard>
  );
};
